import { Component, EventEmitter, Input, Output } from '@angular/core'

@Component({
    selector: 'app-multi-select-box',
    templateUrl: './multi-select-box.component.html',
    styleUrls: ['./multi-select-box.component.scss'],
})
export class MultiSelectBoxComponent<T extends { name?: string | undefined }> {
    _selected: boolean[] | null | undefined
    _allSelected = false

    @Input()
    title: string | undefined
    @Input()
    showSelectAll: boolean | undefined = true
    /** When set, unchecked items for which this returns true cannot be selected. */
    @Input()
    isItemDisabled: ((item: T, selected: T[]) => boolean) | undefined

    private _selectedNameSet: Set<string> | null = null
    /** Optional controlled selection by item name (keeps checkboxes in sync with parent). */
    @Input()
    set selectedNames(names: string[] | null | undefined) {
        this._selectedNameSet = names ? new Set(names) : null
        this.applySelectedNames()
    }

    private _items: T[] | null | undefined
    get items(): T[] | null | undefined {
        return this._items
    }
    @Input()
    set items(value: T[] | null | undefined) {
        this._items = value
        this._selected = value?.map(() => false)
        this._allSelected = false
        this.applySelectedNames()
    }

    @Output() selected = new EventEmitter<T[]>()

    change(index: number): void {
        if (this._allSelected) this._allSelected = false
        if (!this._selected || !this._items) return

        const willSelect = !this._selected[index]
        if (willSelect && this.isDisabledAt(index)) {
            return
        }
        this._selected[index] = willSelect
        this.updateSelectedOutput()
    }

    toggleAll(): void {
        if (!this._selected || !this._items) return

        this._allSelected = !this._allSelected
        if (!this._allSelected) {
            this._selected.fill(false)
            this.updateSelectedOutput()
            return
        }

        // Greedy select: keep items that do not conflict with already chosen ones.
        this._selected.fill(false)
        for (let i = 0; i < this._items.length; i++) {
            if (!this.isDisabledAt(i)) {
                this._selected[i] = true
            }
        }
        this.updateSelectedOutput()
    }

    isDisabledAt(index: number): boolean {
        if (!this.isItemDisabled || !this._items || !this._selected) return false
        if (this._selected[index]) return false
        const selected = this.currentSelectedItems()
        return this.isItemDisabled(this._items[index], selected)
    }

    private applySelectedNames(): void {
        if (!this._items || !this._selected || !this._selectedNameSet) return
        for (let i = 0; i < this._items.length; i++) {
            const name = this._items[i].name
            this._selected[i] = !!name && this._selectedNameSet.has(name)
        }
        this._allSelected =
            this._selected.length > 0 && this._selected.every(selected => selected)
    }

    private currentSelectedItems(): T[] {
        if (!this._items || !this._selected) return []
        return this._items.filter((_item, index) => this._selected?.[index])
    }

    private updateSelectedOutput(): void {
        this.selected.emit(this.currentSelectedItems())
    }
}
